// helper: pretty bytes
const fmt = (n: number) => `${n.toLocaleString()}B`;

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

// Sleep helper
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Exponential backoff with jitter
const getBackoffMs = (attempt: number): number => {
    const base = Math.min(INITIAL_BACKOFF_MS * Math.pow(2, attempt), MAX_BACKOFF_MS);
    const jitter = base * 0.2 * Math.random(); // ±20% jitter
    return base + jitter;
};

// Probe current committed offset on the session.
// GCS responds 308 and `Range: bytes=0-<last>` if any data committed.
async function probeOffset(sessionUrl: string, total: number): Promise<number> {
    const res = await fetch(sessionUrl, {
        method: "PUT",
        headers: {"Content-Range": `bytes */${total}`, "Content-Length": "0"},
    });
    const range = res.headers.get("Range");
    let offset = 0;
    if (range) {
        const m = range.match(/bytes=(\d+)-(\d+)/);
        if (m) offset = parseInt(m[2], 10) + 1;
    }
    console.debug("[probe] status=%s Range=%s -> offset=%d", res.status, range, offset);
    return offset;
}

// Check if error is retryable (network errors, 5xx, 429)
function isRetryableError(error: unknown, status?: number): boolean {
    // Network errors (TypeError from fetch)
    if (error instanceof TypeError) return true;
    // Server errors or rate limiting
    if (status && (status >= 500 || status === 429)) return true;
    return false;
}

export async function uploadOneFileToGCS(
    sessionUrl: string,
    file: File,
    docId: string,
    initContentType?: string,
    onProgress?: (docId: string, percent: number) => void
): Promise<void> {
    const total = file.size;
    const contentType = (initContentType ?? file.type) || "application/octet-stream";
    const chunkSize = 100 * 1024 * 1024; // 100 MiB (multiple of 256 KiB)

    console.log(
        "[upload] start doc=%s name=%s total=%s type(file)=%s type(used)=%s",
        docId, file.name, fmt(total), file.type || "<empty>", contentType
    );

    let offset = 0;
    let consecutiveErrors = 0;

    while (offset < total) {
        const endExclusive = Math.min(offset + chunkSize, total);
        const chunk = file.slice(offset, endExclusive);
        const start = offset;
        const end = endExclusive - 1;

        console.debug(
            "[upload] PUT Range bytes %d-%d/%d (%s)",
            start, end, total, fmt(chunk.size)
        );

        let res: Response;

        // Retry loop for this chunk
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                res = await fetch(sessionUrl, {
                    method: "PUT",
                    headers: {
                        "Content-Type": contentType,
                        "Content-Range": `bytes ${start}-${end}/${total}`,
                    },
                    body: chunk,
                });

                // Success or expected status - break retry loop
                if (res.ok || res.status === 308 || res.status === 409 || res.status === 416) {
                    consecutiveErrors = 0;
                    break;
                }

                // Retryable server error
                if (isRetryableError(null, res.status)) {
                    if (attempt < MAX_RETRIES) {
                        const backoff = getBackoffMs(attempt);
                        console.warn(
                            "[upload] Retryable error %d for %s, attempt %d/%d, waiting %dms",
                            res.status, docId, attempt + 1, MAX_RETRIES, backoff
                        );
                        await sleep(backoff);
                        continue;
                    }
                }

                // Non-retryable error - break immediately
                break;

            } catch (err) {
                // Network error - retryable
                if (isRetryableError(err) && attempt < MAX_RETRIES) {
                    const backoff = getBackoffMs(attempt);
                    console.warn(
                        "[upload] Network error for %s, attempt %d/%d, waiting %dms: %s",
                        docId, attempt + 1, MAX_RETRIES, backoff, err
                    );
                    await sleep(backoff);

                    // Probe server to see what was actually received
                    try {
                        offset = await probeOffset(sessionUrl, total);
                        console.log("[upload] After network error, server has offset=%d", offset);
                    } catch {
                        // Probe failed, continue with current offset
                    }
                    continue;
                }
                throw err;
            }
        }

        // Process response
        const bodyText = (!res!.ok && res!.status !== 308) ? await res!.text().catch(() => "") : "";

        if (res!.status === 308) {
            // Partial OK — trust server's Range to advance offset
            const range = res!.headers.get("Range");
            console.debug("[upload] 308 Range=%s", range);
            if (range) {
                const m = range.match(/bytes=(\d+)-(\d+)/);
                if (m) {
                    const last = parseInt(m[2], 10);
                    offset = last + 1;
                } else {
                    offset = end + 1;
                }
            } else {
                offset = end + 1;
            }
        } else if (res!.ok) {
            // Final chunk: 200/201
            offset = end + 1;
            console.debug("[upload] final chunk accepted. offset=%d", offset);
        } else {
            // Error path
            const hdrs = Object.fromEntries(res!.headers.entries());
            console.error(
                "[upload] ERROR doc=%s name=%s status=%s %s\nheaders=%o\nbody=%s",
                docId, file.name, res!.status, res!.statusText, hdrs, bodyText
            );

            if (res!.status === 409 || res!.status === 416) {
                console.warn("[upload] Range mismatch (409/416). Probing server offset and retrying…");
                offset = await probeOffset(sessionUrl, total);
                consecutiveErrors++;
                if (consecutiveErrors > MAX_RETRIES) {
                    throw new Error(`Too many consecutive range errors for ${docId}`);
                }
                continue;
            }

            if (res!.status === 403) {
                console.warn(
                    "[upload] 403 AccessDenied. Possible causes: expired session, mismatched Content-Range start, or CORS."
                );
            }

            throw new Error(`Chunk upload failed for ${docId}: ${res!.status} ${res!.statusText}`);
        }

        const pct = Math.min(100, Math.round((offset / total) * 100));
        console.log("[upload] %s %d%% (%s / %s)", docId, pct, fmt(offset), fmt(total));
        onProgress?.(docId, pct);
    }

    console.log("[upload] complete doc=%s name=%s", docId, file.name);
}

/**
 * Upload multiple files in parallel with concurrency control.
 * This is more robust than sequential uploads and faster.
 */
export async function uploadFilesInParallel(
    uploads: Array<{
        sessionUrl: string;
        file: File;
        docId: string;
        contentType?: string;
    }>,
    options: {
        concurrency?: number;
        onProgress?: (docId: string, percent: number) => void;
        onFileComplete?: (docId: string) => void;
        onFileError?: (docId: string, error: Error) => void;
    } = {}
): Promise<{ succeeded: string[]; failed: Array<{ docId: string; error: Error }> }> {
    const {concurrency = 3, onProgress, onFileComplete, onFileError} = options;

    const succeeded: string[] = [];
    const failed: Array<{ docId: string; error: Error }> = [];

    // Create a queue of pending uploads
    const pending = [...uploads];
    const inFlight = new Set<Promise<void>>();

    const processOne = async (upload: typeof uploads[0]) => {
        try {
            await uploadOneFileToGCS(
                upload.sessionUrl,
                upload.file,
                upload.docId,
                upload.contentType,
                onProgress
            );
            succeeded.push(upload.docId);
            onFileComplete?.(upload.docId);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            failed.push({docId: upload.docId, error});
            onFileError?.(upload.docId, error);
            console.error("[parallel-upload] Failed:", upload.docId, error);
        }
    };

    // Process with concurrency limit
    while (pending.length > 0 || inFlight.size > 0) {
        // Start new uploads up to concurrency limit
        while (pending.length > 0 && inFlight.size < concurrency) {
            const upload = pending.shift()!;
            const promise = processOne(upload).finally(() => {
                inFlight.delete(promise);
            });
            inFlight.add(promise);
        }

        // Wait for at least one to complete before continuing
        if (inFlight.size > 0) {
            await Promise.race(inFlight);
        }
    }

    return {succeeded, failed};
}
